import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface AdminSignature {
  id: string;
  user_id: string;
  signature_type: "drawn" | "typed" | "certificate";
  signature_data: string;
  certificate_info: Record<string, string> | null;
  created_at: string;
  updated_at: string;
}

export function useAdminSignature() {
  return useQuery({
    queryKey: ["admin-signature"],
    queryFn: async () => {
      const { data, error } = await supabase.from("admin_signatures").select("*").maybeSingle();
      if (error) throw error;
      return data as AdminSignature | null;
    },
  });
}

export function useSaveAdminSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      signature_type: "drawn" | "typed" | "certificate";
      signature_data: string;
      certificate_info?: Record<string, string> | null;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("admin_signatures")
        .upsert({ user_id: userData.user.id, ...payload }, { onConflict: "user_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-signature"] });
      toast.success("Assinatura salva com sucesso");
    },
    onError: (e) => toast.error("Erro ao salvar: " + (e as Error).message),
  });
}

export function useDeleteAdminSignature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("admin_signatures")
        .delete()
        .eq("user_id", userData.user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-signature"] });
      toast.success("Assinatura removida");
    },
    onError: (e) => toast.error("Erro ao remover: " + (e as Error).message),
  });
}
